import { message } from 'antd';
import React from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { BroadCast, BroadCastFormType } from 'src/models/broadCast/type';
import { BroadCastRepository } from 'src/repositories/braodCast/interface';
import { useBroadCastRepository } from 'src/repositories/braodCast/repository';
import { StorageRepository } from 'src/repositories/storage/interface';
import { useStorageRepository } from 'src/repositories/storage/repository';
import { useBroadCastCacheMutator } from './cache';
import {
  LoginAccountState,
  useLoginAccount,
} from 'src/globalStates/loginAccount';
import { 
  convertSegmentConditions,
  validateContentByType
} from './util';

export const useBroadCastForm = () => {
  const repository = useBroadCastRepository();
  const storageRepo = useStorageRepository();
  const navigator = useNavigate();
  const account = useLoginAccount();
  const mutator = useBroadCastCacheMutator();

  return React.useMemo(
    () =>
      createBroadCastFormConstructor({
        repository,
        storageRepo,
        navigator,
        account,
        mutator,
      }),
    [repository, storageRepo, navigator, account, mutator]
  );
};



const createBroadCastFormConstructor = ({
  repository,
  storageRepo,
  navigator,
  account,
  mutator,
}: {
  repository: BroadCastRepository;
  storageRepo: StorageRepository;
  navigator: NavigateFunction;
  account: LoginAccountState;
  mutator: ReturnType<typeof useBroadCastCacheMutator>;
}) => ({
  async submitNormal(
    value: BroadCastFormType,
    status: 'draft' | 'reserved',
    image?: File,
    templateImageUrl?: string | null
  ) {
    try {
      // 1.共通バリデーション実行
      validateContentByType(value, status, image, templateImageUrl);
  
      // 2.通常配信固有のバリデーション
      if (value.sendAll === undefined) {
        throw new Error('送信対象を選択してください');
      }
  
      if (value.sendAll === false && (!value.sendMCustomerLineIds || value.sendMCustomerLineIds.length === 0)) {
        throw new Error('ファイルがアップロードされていません');
      }
      
  
      const _needImage =
        value.type === 'image' || value.type === 'textAndImage';

      const _needTempImage =
        value.type === 'template' || value.type === 'textAndTemplate';

    var _imageUrl = _needImage
        ? value.broadCast?.message?.imageUrl ?? null
        : _needTempImage
        ? value.broadCast?.message?.tempImageUrl ?? null
        : null;

      if (image) {
        _imageUrl = await storageRepo.upload({
          file: image!,
          fileName: image!.name,
          collPath: 'BroadCasts',
        });
      } else {
        if (_needImage) {
          if (templateImageUrl) {
            _imageUrl = templateImageUrl;
          } else {
            _imageUrl = value.broadCast!.message!.imageUrl;
          }
        }
        if (_needTempImage) {
          if (templateImageUrl) {
            _imageUrl = templateImageUrl;
          } else {
            _imageUrl = value.broadCast!.message!.tempImageUrl;
          }
        }
      }

      const _message = {
        altText:
          value.type === 'template' || value.type === 'textAndTemplate'
            ? value.altText
            : null,
        imageUrl:
          value.type === 'image' || value.type === 'textAndImage'
            ? _imageUrl
            : null,
        tempImageUrl:
          value.type === 'template' || value.type === 'textAndTemplate'
            ? _imageUrl
            : null,
        tempLabel:
          value.type === 'template' || value.type === 'textAndTemplate'
            ? value.tempLabel
            : null,
        tempUri:
          value.type === 'template' || value.type === 'textAndTemplate'
            ? value.tempUri
            : null,
        text:
          value.type === 'text' ||
          value.type === 'textAndImage' ||
          value.type === 'textAndTemplate'
            ? value.text
            : null,
        type: value.type,
      };

      if (!!!value.broadCast) {
        const _broadCast: BroadCast = {
          createdAt: new Date(),
          customerSpecificDeliverySettings: value.sendAll
            ? null
            : value.customerSpecificDeliverySettings ?? null,
          deletedAt: null,
          documentStatus: 'active',
          message: _message,
          requestIds: [],
          results: [],
          sendAt: status === 'draft' ? null : value.sendAt?.toDate() ?? null,
          sendMCustomerLineIds: value.sendAll
            ? null
            : value.sendMCustomerLineIds ?? [],
          sentAt: null,
          status: status,
          storeIds: value.storeIds ?? [],
          title: value.title,
          updatedAt: new Date(),
          deliveryType: 'normal',
          segmentConditions: null,
          segmentResultId: null,
          segmentResultCount: null,
          storeSelectionStrategy: null,
          segmentJobStatus: null,
        };
  
        await repository.createDoc(_broadCast, account);

        mutator.mutateList({ account });
        
        message.success('新規作成しました');
      } else {
        const _broadCast: BroadCast = {
          ...value.broadCast,
          customerSpecificDeliverySettings: value.sendAll
            ? null
            : value.customerSpecificDeliverySettings ?? null,
          message: _message,
          sendAt: status === 'draft' ? null : value.sendAt?.toDate() ?? null,
          sendMCustomerLineIds: value.sendAll
            ? null
            : value.sendMCustomerLineIds ?? [],
          status: status,
          storeIds: value.storeIds ?? [],
          title: value.title,
          updatedAt: new Date(),
        };
  
        await repository.update(_broadCast, account);

        mutator.mutateItem(
          { bId: value.broadCast?.id },
          { broadCast: value },
          true
        );

        message.success('更新しました');
      }
  
      navigator('../');
    } catch (error: any) {
      message.error(error?.toString() ?? 'エラーが発生しました');
      return;
    }
  },
  async submitSegment(
    value: BroadCastFormType,
    status: 'draft' | 'reserved',
    image?: File,
    templateImageUrl?: string | null,
    shouldStartSearch?: boolean
  ) {
    try {
      // 1.共通バリデーション実行
      validateContentByType(value, status, image, templateImageUrl);
  
      // 2.セグメント配信固有のバリデーション
      // 配信先検索時（draft）は最小限のチェック
      if (status === 'draft') {
        // セグメント条件が1つ以上設定されているかチェック
        const _segmentConditions = value.segmentConditions;

        if (!_segmentConditions || Object.keys(_segmentConditions).length === 0) {
          throw new Error('セグメント条件を1つ以上設定してください');
        }
      
        // 特定日来店のバリデーション
        if (_segmentConditions.hasOwnProperty('specificDateVisit')) {
          const targetDates = _segmentConditions.specificDateVisit?.targetDates;
          if (!targetDates || !Array.isArray(targetDates) || targetDates.length === 0) {
            throw new Error('特定日来店の対象日付を1つ以上選択してください');
          }
        }

        // 来店パターンのバリデーション
        if (_segmentConditions.hasOwnProperty('visitPattern')) {
          const { dateRange, visitCount } = _segmentConditions.visitPattern || {};
          if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) {
            throw new Error('来店パターンの期間を設定してください');
          }
          if (!visitCount || !visitCount.count || !visitCount.operator) {
            throw new Error('来店パターンの回数と条件を設定してください');
          }
        }

        // 顧客ランクのバリデーション
        if (_segmentConditions.hasOwnProperty('customerRank')) {
          if (!Array.isArray(_segmentConditions.customerRank) || _segmentConditions.customerRank.length === 0) {
            throw new Error('顧客ランクを1つ以上選択してください');
          }
        }

        // 商品購入パターンのバリデーション
        if (_segmentConditions.hasOwnProperty('itemPreferences')) {
          const { targetItemIds, purchaseCount } = _segmentConditions.itemPreferences || {};
          if (!targetItemIds || !Array.isArray(targetItemIds) || targetItemIds.length === 0) {
            throw new Error('対象商品を1つ以上選択してください');
          }
          if (!purchaseCount || !purchaseCount.count || !purchaseCount.operator) {
            throw new Error('商品購入パターンの回数と条件を設定してください');
          }
        }

        // ポイントカードのバリデーション
        if (_segmentConditions.hasOwnProperty('pointCard')) {
          const { cardIds, pointRange } = _segmentConditions.pointCard || {};
          if (!cardIds || !Array.isArray(cardIds) || cardIds.length === 0) {
            throw new Error('ポイントカード種類を1つ以上選択してください');
          }
          if (!pointRange ||
            pointRange.min === undefined || pointRange.min === null ||
            pointRange.max === undefined || pointRange.max === null) {
            throw new Error('ポイント範囲の最小値と最大値を両方設定してください');
          }
          if (pointRange.min !== undefined && pointRange.max !== undefined && pointRange.min > pointRange.max) {
            throw new Error('最小ポイントは最大ポイント以下である必要があります');
          }
        }

        // アンケート回答のバリデーション
        if (_segmentConditions.hasOwnProperty('surveyAnswers')) {
          if (!Array.isArray(_segmentConditions.surveyAnswers) || _segmentConditions.surveyAnswers.length === 0) {
            throw new Error('アンケート条件を1つ以上設定してください');
          }

          _segmentConditions.surveyAnswers.forEach((answer: any, index: number) => {
            // answer自体がundefinedまたはnullの場合
            if (!answer) {
              throw new Error(`アンケート条件${index + 1}の内容を設定してください`);
            }

            if (!answer.surveyId) {
              throw new Error(`アンケート条件${index + 1}でアンケートを選択してください`);
            }
            if (!answer.questionId) {
              throw new Error(`アンケート条件${index + 1}で質問を選択してください`);
            }
            if (!answer.answerCondition || !answer.answerCondition.type) {
              throw new Error(`アンケート条件${index + 1}で回答条件を設定してください`);
            }

            // 質問タイプごとの条件値チェック
            const condition = answer.answerCondition;

            if (condition.type === 'text') {
              if (!condition.textContains || condition.textContains.trim() === '') {
                throw new Error(`アンケート条件${index + 1}で含む文字列を入力してください`);
              }
            } 
            else if (condition.type === 'number') {
              if (!condition.numberRange || condition.numberRange.min === undefined || condition.numberRange.max === undefined) {
                throw new Error(`アンケート条件${index + 1}で最小値と最大値の両方を設定してください`);
              }
              // 最小値 <= 最大値のチェックも追加
              if (condition.numberRange.min > condition.numberRange.max) {
                throw new Error(`アンケート条件${index + 1}の最小値は最大値以下である必要があります`);
              }
            } 
            else if (condition.type === 'radio' || condition.type === 'checkbox') {
              if (!condition.selectedOptions || !Array.isArray(condition.selectedOptions) || condition.selectedOptions.length === 0) {
                throw new Error(`アンケート条件${index + 1}で選択肢を1つ以上選択してください`);
              }
            }
          });
        }

        // 送信店舗選択の第1優先が設定されているかチェック
        if (!value.storeSelectionStrategy) {
          throw new Error('送信店舗選択ルールを選択してください');
        }
      }

      // 配信確定時（reserved）は追加チェック
      if (status === 'reserved') {
        // 配信対象者が検索済みかチェック
        if (!value.broadCast?.segmentJobStatus || value.broadCast.segmentJobStatus !== 'completed') {
          throw new Error('配信先を検索してから配信確定を行ってください');
        }
      }
  
      // 画像処理（submitNormalと同じ）
      const _needImage = value.type === 'image' || value.type === 'textAndImage';
      const _needTempImage = value.type === 'template' || value.type === 'textAndTemplate';
  
      var _imageUrl = _needImage
        ? value.broadCast?.message?.imageUrl ?? null
        : _needTempImage
        ? value.broadCast?.message?.tempImageUrl ?? null
        : null;
  
      if (image) {
        _imageUrl = await storageRepo.upload({
          file: image!,
          fileName: image!.name,
          collPath: 'BroadCasts',
        });
      } else {
        if (_needImage) {
          if (templateImageUrl) {
            _imageUrl = templateImageUrl;
          } else {
            _imageUrl = value.broadCast!.message!.imageUrl;
          }
        }
        if (_needTempImage) {
          if (templateImageUrl) {
            _imageUrl = templateImageUrl;
          } else {
            _imageUrl = value.broadCast!.message!.tempImageUrl;
          }
        }
      }
  
      // メッセージ構築
      const _message = {
        altText:
          value.type === 'template' || value.type === 'textAndTemplate'
            ? value.altText
            : null,
        imageUrl:
          value.type === 'image' || value.type === 'textAndImage'
            ? _imageUrl
            : null,
        tempImageUrl:
          value.type === 'template' || value.type === 'textAndTemplate'
            ? _imageUrl
            : null,
        tempLabel:
          value.type === 'template' || value.type === 'textAndTemplate'
            ? value.tempLabel
            : null,
        tempUri:
          value.type === 'template' || value.type === 'textAndTemplate'
            ? value.tempUri
            : null,
        text:
          value.type === 'text' ||
          value.type === 'textAndImage' ||
          value.type === 'textAndTemplate'
            ? value.text
            : null,
        type: value.type,
      };
  
      // データ保存
      if (!!!value.broadCast) {
        const _broadCast: BroadCast = {
          createdAt: new Date(),
          customerSpecificDeliverySettings: null,
          deletedAt: null,
          documentStatus: 'active',
          message: _message,
          requestIds: [],
          results: [],
          sendAt: status === 'draft' ? null : value.sendAt?.toDate() ?? null,
          sendMCustomerLineIds: null,
          sentAt: null,
          status: status,
          storeIds: value.storeIds ?? [],
          title: value.title ?? '',
          updatedAt: new Date(),
          deliveryType: 'segment',
          segmentConditions: convertSegmentConditions(value.segmentConditions),
          segmentResultId: null,
          segmentResultCount: null,
          storeSelectionStrategy: value.storeSelectionStrategy || null,
          segmentJobStatus: (status === 'draft' && shouldStartSearch) ? 'processing' : null,
        };

        await repository.createDoc(_broadCast, account);
        mutator.mutateList({ account });
        
        if (status === 'draft') {
          if (shouldStartSearch) {
            message.success('配信先検索を開始しました');
          } else {
            message.success('下書きを保存しました');
          }
        } else {
          message.success('配信を設定しました');
        }
      } else {
        const _broadCast: BroadCast = {
          ...value.broadCast,
          customerSpecificDeliverySettings: null,
          message: _message,
          sendAt: status === 'draft' ? null : value.sendAt?.toDate() ?? null,
          sendMCustomerLineIds: null,
          status: status,
          storeIds: value.storeIds ?? [],
          title: value.title ?? '',
          updatedAt: new Date(),
          segmentConditions: convertSegmentConditions(value.segmentConditions),
          segmentResultId: shouldStartSearch ? null : value.broadCast.segmentResultId,
          segmentResultCount: shouldStartSearch ? null : value.broadCast.segmentResultCount,
          storeSelectionStrategy: value.storeSelectionStrategy || null,
          segmentJobStatus: (status === 'draft' && shouldStartSearch)
            ? 'processing'
            : value.broadCast.segmentJobStatus,
        };
  
        await repository.update(_broadCast, account);
        mutator.mutateItem(
          { bId: value.broadCast?.id },
          { broadCast: value },
          true
        );
  
        if (status === 'draft') {
          if (shouldStartSearch) {
            message.success('配信先検索を開始しました');
          } else {
            message.success('下書きを保存しました');
          }
        } else {
          message.success('配信を更新しました');
        }
      }
  
      navigator('../');
    } catch (error: any) {
      message.error(error?.toString() ?? 'エラーが発生しました');
      return;
    }
  }
});
